import type { CollectionAdminOptions, CollectionConfig, CollectionSlug, Field, RelationshipField, TextField, LabelFunction, StaticLabel, CollectionBeforeOperationHook, GlobalConfig, GlobalAdminOptions, GlobalSlug } from 'payload'
import { enableExport, enableNotification, enableCascadeDelete, ExportableProps, NotifyProps, CascadeDeleteProps } from '@utils/collections'
import { NEXT_PUBLIC_URL, isDebugging, isDeveloping } from '@utils/constants'
import { createAccess, createGlobalAccess } from '@utils/groups'
import { mapCollectionFields, virtualJsonField } from '@utils/fields'
import { trim } from '@utils/misc'
import { mergeObjects } from '@utils/objects'
import { slugifyFilename as slugifyWithDots } from '@utils/slug'

export type CreateCollectionConfigProps = Omit<CollectionConfig, 'fields'> & {
  fields: CustomPayloadField<Field>[];
  admin?: ExportableProps & NotifyProps;
  cascadeDelete?: CascadeDeleteProps[];
};

// Create a Collection
export const CreateCollectionConfig = (collection: CreateCollectionConfigProps): CollectionConfig => {
  collection.admin = collection.admin || {};
  collection.hooks = collection.hooks || {};

  const lookForFieldName = (fields: Field[], names: string[]): string => {
    for (let i = 0; i < fields.length; i++) {
      const field: any = fields[i];

      // For tabs
      if (field?.tabs) {
        for (let j = 0; j < field?.tabs.length; j++) {
          const tab = field?.tabs[j];
          const maybeName = lookForFieldName(tab?.fields || [], names);

          if (maybeName) {
            return maybeName;
          }
        }

        continue;
      }

      // For subfields
      if (field?.fields) {
        const maybeName = lookForFieldName(field.fields, names);
        if (maybeName) {
          return maybeName;
        }

        continue;
      }

      // Check for name
      if (field?.name && names.includes(field?.name)) {
        return field.name;
      }
    }

    return '';
  }

  /**
   * Admin Title
   * Will try to create a admin.useAsTitle based on fields
   */
  if (! collection.admin?.useAsTitle) {
    const titleField = lookForFieldName(collection.fields, ['title', 'name']);
    if (titleField) {
      collection.admin.useAsTitle = titleField;
    }
  }

  /**
   * Parse fields
   * Common field options
   */
  const relationships: (Pick<RelationshipField, 'name' | 'label'> & { virtual: RelationshipField['name']; hasMany: RelationshipField['hasMany'] })[] = [];
  collection.fields = mapCollectionFields(collection.fields, (field) => {
    // Required SELECT should be non clearable by default
    if (field?.type === 'select' && field.required && typeof field?.admin?.isClearable === 'undefined') {
      field.admin = mergeObjects(field.admin, { isClearable: false });
    }

    // Hidden fields (non UI) should not appear on list and filter by default
    if (field?.type !== 'ui' && field?.admin?.hidden) {
      field.admin.disableListColumn = (typeof field.admin?.disableListColumn !== 'undefined') ? field.admin.disableListColumn : true;
      field.admin.disableListFilter = (typeof field.admin?.disableListFilter !== 'undefined') ? field.admin.disableListFilter : true;
    }

    // Relationships should have a data VIRTUAL field to hold data
    if (field?.type === 'relationship') {
      relationships.push({
        name: field.name,
        label: field.label,
        virtual: field.hasMany ? `${field.name}_items` : `${field.name}_data`,
        hasMany: field.hasMany,
      });

      if (! field?.admin?.components?.Cell) {
        field.admin = mergeObjects(field.admin, {
          components: {
            Cell: {
              path: '@components/(payload)/fields/cell',
              clientProps: {
                format: 'relationship'
              }
            }
          }
        });
      }
    }

    return field;
  });

  for (let i = 0; i < relationships.length; i++) {
    const relationship = relationships[i];
    collection.fields.push(...virtualJsonField({ name: relationship.virtual, label: relationship.label, admin: { disableListColumn: true, disableListFilter: true } }));
  }

  if (relationships.length) {
    collection.hooks.afterRead = collection.hooks.afterRead || [];
    collection.hooks.afterRead.unshift(
      ({ doc }) => {
        // Attention! Will not work on 'update' or relative hooks
        for (let i = 0; i < relationships.length; i++) {
          const relationship = relationships[i];
          doc[ relationship.virtual ] = doc[relationship.name] || ( relationship.hasMany ? [] : {} );
        }

        return doc;
      }
    );

    collection.hooks.beforeOperation = collection.hooks.beforeOperation || [];
    collection.hooks.beforeOperation.unshift(
      (({ args, operation }) => {
        if (operation !== 'read' && operation !== 'refresh') {
          return args;
        }

        if (args && ('depth' in args || 'currentDepth' in args)) {
          const depth = args?.currentDepth || ( args?.depth || 0 );
          args.depth = ((args?.depth ?? 0) > depth) ? args?.depth : (depth + 1);
        }

        return args;
      }) as CollectionBeforeOperationHook<CollectionSlug>
    );
  }

  /**
   * Upload collections
   */
  if (typeof collection.upload !== 'undefined') {
    const fullUrlField: CustomPayloadField<TextField> = {
      type: 'text',
      name: 'fullUrl',
      label: 'URL Completa',
      admin: {
        hidden: true,
        exportable: true,
      },
    };

    collection.fields.push(fullUrlField);

    collection.hooks.beforeOperation = collection.hooks.beforeOperation || [];
    collection.hooks.beforeOperation.unshift(
      ({ req, operation }) => {
        if ((operation === 'create' || operation === 'update') && req.file) {
          req.file.name = slugifyWithDots(req.file.name);
        }
      }
    );

    collection.hooks.afterRead = collection.hooks.afterRead || [];
    collection.hooks.afterRead.unshift(
      ({ doc }) => {
        doc.fullUrl = `${ NEXT_PUBLIC_URL }/${ trim( doc.url, '/' ) }`;
        return doc;
      }
    );
  }

  /**
   * Notifiable collections
   */
  if (collection.admin?.notifiable) {
    const notifiable = collection.admin.notifiable;

    delete collection.admin.notifiable;

    collection = enableNotification(collection, { notifiable });
  }

  /**
   * Importable/Exportable collections
   */
  if (typeof collection.admin !== 'undefined' && 'exportable' in collection.admin) {
    const exportable = collection.admin?.exportable;

    delete collection.admin.exportable;

    collection = enableExport(collection, { exportable });
  }

  /**
   * Cascade delete collections
   */
  if (collection.cascadeDelete && collection.cascadeDelete.length > 0) {
    const cascadeDelete = collection.cascadeDelete;

    delete collection.cascadeDelete;

    collection = enableCascadeDelete(collection, cascadeDelete);
  }

  return collection;
};

// Create a Collection without admin
export const CreatePrivateCollectionConfig = (
  slug: CollectionSlug,
  singular: LabelFunction | StaticLabel,
  plural: LabelFunction | StaticLabel,
  config: Omit<CreateCollectionConfigProps, 'slug'>
): CollectionConfig => {
  const defaultConfig: CreateCollectionConfigProps = {
    slug,
    labels: {
      singular,
      plural,
    },
    admin: {
      group: isDebugging() ? 'Desenvolvimento' : false,
      hideAPIURL: ! isDeveloping(),
      notifiable: false,
      exportable: false,
    },
    access: createAccess({ create: 'INTRANET_ADMIN', read: 'INTRANET_ADMIN', update: 'INTRANET_ADMIN' }),
    lockDocuments: false,
    disableDuplicate: true,
    fields: [],
    cascadeDelete: [],
  };

  return CreateCollectionConfig(mergeObjects(defaultConfig, config));
};

/**
 * Create a Collection with admin
 * Default: title as Title, hide API, without duplication and accessible by all (only deletable by admins)
 */
export const CreatePublicCollectionConfig = (
  slug: CollectionSlug,
  singular: LabelFunction | StaticLabel,
  plural: LabelFunction | StaticLabel,
  group: CollectionAdminOptions['group'],
  config: Omit<CreateCollectionConfigProps, 'slug'>
): CollectionConfig => {
  const defaultConfig: CreateCollectionConfigProps = {
    slug,
    labels: {
      singular,
      plural,
    },
    admin: {
      group,
      hideAPIURL: ! isDeveloping(),
      notifiable: false,
      exportable: false,
    },
    access: createAccess(),
    lockDocuments: false,
    disableDuplicate: true,
    fields: [],
    cascadeDelete: [],
  };

  return CreateCollectionConfig(mergeObjects(defaultConfig, config));
};

/**
 * Extended global configuration props that includes custom field types
 * This allows for additional field processing and virtual field support
 */
export type CreateGlobalConfigProps = Omit<GlobalConfig, 'fields'> & {
  fields: CustomPayloadField<Field>[];
};

/**
 * Creates a private global configuration (admin-only access)
 *
 * Private globals are accessible by admins only to be used in Local API (by default).
 * You should set permissions to parent global in that cases.
 *
 * - Group under 'Desenvolvimento' when debugging, otherwise hide from admin.
 * - Hide API screens in production.
 *
 * @param slug - Unique identifier for the global
 * @param label - Label for the global
 * @param group - Admin group to organize the global in the admin interface
 * @param config - Additional configuration options
 * @returns A configured GlobalConfig with restricted access
 */
export const CreateGlobalConfig = (
  slug: GlobalSlug,
  label: LabelFunction | StaticLabel,
  group: GlobalAdminOptions['group'],
  config: Omit<CreateGlobalConfigProps, 'slug'>
): GlobalConfig => {
  const defaultConfig: CreateGlobalConfigProps = {
    slug,
    label,
    admin: {
      group,
      hideAPIURL: ! isDeveloping(),
    },
    graphQL: false,
    access: createGlobalAccess({ read: 'INTRANET_ADMIN', update: 'INTRANET_ADMIN' }),
    fields: [],
    lockDocuments: false,
  };

  return mergeObjects(defaultConfig, config);
};
